import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';

import momentTZ from 'js-utils/moment-timezone';
import EnhancedEventCard from 'eventbrite_design_system/eventCard/EnhancedEventCard';

import {
    EVENT_SHAPE_PROPTYPE,
    FEATURE_FLAGS_SHAPE_PROP_TYPE,
} from '../constants';
import EventCardGroupEdit from './EventCardGroupEdit';

import './eventCardGroup.scss';

const getEventCards = ({
    events,
    cardWrapperClasses,
    handleOnLike,
    handleOnView,
    handleOnWillShare,
    handleOnDidShare,
    userCollections,
    onShowCreateCollectionView,
    onShowAddToCollectionView,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
    isAuthenticated,
    isMobile,
    featureFlags,
}) =>
    events.map(
        ({
            id,
            name,
            imageUrl,
            customImage,
            customTitle,
            customDescription,
            ...eventCardData
        }) => (
            <li
                key={`${imageUrl}-${isMobile ? 'mobile' : 'desktop'}`}
                className={cardWrapperClasses}
            >
                <EnhancedEventCard
                    id={id}
                    onSaveEvent={handleOnLike}
                    onClick={handleOnView.bind(null, id)}
                    onWillShareClick={handleOnWillShare}
                    onDidShareClick={handleOnDidShare.bind(null, id)}
                    isAuthenticated={isAuthenticated}
                    userCollections={userCollections}
                    onShowCreateCollectionView={onShowCreateCollectionView}
                    onShowAddToCollectionView={onShowAddToCollectionView}
                    onAddOrRemoveEventToCollection={
                        onAddOrRemoveEventToCollection
                    }
                    onCreateCollection={onCreateCollection}
                    isConsumer={true}
                    {...eventCardData}
                    type="grid"
                    style="standard"
                    // Apply editorial collection overrides
                    name={customTitle || name}
                    imageUrl={customImage ? customImage.url : imageUrl}
                    shouldConvertToUserTimezone={true}
                />
                <p className="eds-text-bl eds-text-weight--regular eds-l-pad-hor-4">
                    {customDescription}
                </p>
            </li>
        ),
    );

// Group by week logic. To be revisited once ideal grouping logic decided
// const getEventsByWeek = ({eventsByWeek, eventCardProps, isMobile}) => (
//     Object.keys(eventsByWeek)
//         .map((week) => (
//             <section key={`${week}-${isMobile ? 'mobile' : 'desktop'}`} className="collection__event-card-group">
//                 <header className="
//                     eds-text-hm
//                     eds-l-mar-bot-8
//                     eds-l-pad-hor-3
//                 ">
//                     {formatWeekForDisplay(week)}
//                 </header>
//                 {
//                     getEventCards({
//                         ...eventCardProps,
//                         events: eventsByWeek[week],
//                         isMobile,
//                     })
//                 }
//             </section>
//         ))
// );

export default class EventCardGroup extends PureComponent {
    static propTypes = {
        /*
         * Array of event objects with data points
         * preformatted for usage in the card
         */
        events: PropTypes.arrayOf(EVENT_SHAPE_PROPTYPE),
        /*
         * Classes that will be applied to div
         * wrapping the EventCard
         */
        cardWrapperClasses: PropTypes.string,
        /*
         * Handler for if the user chooses to
         * unlike a liked event
         */
        handleOnLike: PropTypes.func,
        /*
         * Handler for when event is clicked
         */
        handleOnView: PropTypes.func,
        /*
         * Handler for when share dialog is open
         */
        handleOnWillShare: PropTypes.func,
        /*
         * Handler for when event shared
         */
        handleOnDidShare: PropTypes.func,
        /*
         * Feature Flags
         */
        featureFlags: FEATURE_FLAGS_SHAPE_PROP_TYPE,
        /*
         * Whether or not the current user is logged in
         */
        isAuthenticated: PropTypes.bool,
        /*
         * Whether the collection is an Editorial Collection
         */
        isQuality: PropTypes.bool,
        /*
         * Whether this is the mobile version
         */
        isMobile: PropTypes.bool,
    };

    static defaultProps = {
        handleOnView: noop,
        handleOnWillShare: noop,
        handleOnDidShare: noop,
        cardWrapperClasses:
            'eds-g-cell eds-g-cell-1-1 eds-g-cell-mn-1-3 eds-g-cell--has-overflow event-card__container',
    };

    render() {
        const {
            events,
            shareOptions,
            cardWrapperClasses,
            handleOnLike,
            handleOnView,
            handleOnWillShare,
            handleOnDidShare,
            featureFlags,
            userCollections,
            onShowCreateCollectionView,
            onShowAddToCollectionView,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            isEditMode,
            isAuthenticated,
            isMobile,
            isQuality,
        } = this.props;

        const eventCardProps = {
            shareOptions,
            handleOnLike,
            handleOnView,
            featureFlags,
            userCollections,
            isAuthenticated,
            handleOnDidShare,
            handleOnWillShare,
            onCreateCollection,
            cardWrapperClasses,
            onShowAddToCollectionView,
            onShowCreateCollectionView,
            onAddOrRemoveEventToCollection,
            featureFlags,
        };

        let cardCmp;

        const eventsSortedByDate = events.sort((eventA, eventB) => {
            const { startDate: a, startTime: b, timezone: timezoneA } = eventA;
            const { startDate: c, startTime: d, timezone: timezoneB } = eventB;

            const dateA = momentTZ.tz(`${a} ${b}`, `${timezoneA}`).valueOf();
            const dateB = momentTZ.tz(`${c} ${d}`, `${timezoneB}`).valueOf();

            //Events often start at the same time and so the
            //sort can output inconsistent values when time
            //equal across two events. In that case simply
            //sort by the event name to get consistent values
            if (dateA - dateB === 0) {
                //Use localeCompare to ensure unicode characters handled
                return eventA.name.localeCompare(eventB.name);
            } else {
                return dateA - dateB;
            }
        });

        // Group by week logic. To be revisited once decide best grouping mechanism
        // const eventsByWeek = groupBy(eventsSortedByDate, ({startDate}) => moment(startDate).startOf('isoWeek').format());

        if (isEditMode) {
            cardCmp = (
                <EventCardGroupEdit
                    name="eventsToRemove"
                    events={eventsSortedByDate}
                    eventCardProps={eventCardProps}
                    isEditMode={isEditMode}
                />
            );
        } else {
            cardCmp = (
                <ul className="eds-g-group">
                    {getEventCards({
                        ...eventCardProps,
                        isMobile,
                        isQuality,
                        events: eventsSortedByDate,
                    })}
                </ul>
            );
        }

        return <div className="event-card-group">{cardCmp}</div>;
    }
}
